import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "w-full space-x-2 flex justify-end" }


const __default__ = {
  name: 'PageItems',
};

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { ItemResource } from '@shared/resources/item';
import { QtyUnitNameMap } from '@shared/types/qty-unit';
import { CategoryResource } from '@shared/resources/category';

import { useI18n } from 'vue-i18n';
import { useModalsStore } from '@/stores/modals';
import { useEmitter } from '@/plugins/mitt/mitt';
import { useCategoriesStore } from '@/stores/dictionary/categories';
import { useSelectClientDataManager } from '@ui-kit/inputs/select/select-data-managers/use-select-client-data-manager';
import { ItemService } from '@/services/item.service';
import { useScanner } from '@/libs/scanner/use-scanner';

import PageWrapper from '@components/page-wrapper/PageWrapper.vue';
import Table from '@ui-kit/table/Table.vue';
import { Filters, TableComponentParams } from '@ui-kit/table/types';
import { TableServerDataManager } from '@ui-kit/table/table-data-managers/table-server-data-manager';
import Button from '@ui-kit/controls/button/Button.vue';
import SingleSelect from '@ui-kit/inputs/select/SingleSelect.vue';
import ModalConfirmation from '@components/modals/ModalConfirmation.vue';
import ModalManageItem from './modals/ModalManageItem.vue';
import { AxiosError } from 'axios';
import Mark from '@ui-kit/mark/Mark.vue';
import { coinsFormat, toNumberFormat } from '@shared/helpers/data-converters/number-converters';
import ItemCompanySelect from '../../partials/item-company-select/ItemCompanySelect.vue';
import { ItemCompanyResource } from '@shared/resources/item-company';
import { useAutoSalePrice } from '@/helpers/auto-sale-price';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const categoriesStore = useCategoriesStore();
const modalsStore = useModalsStore();
const emitter = useEmitter();
const { t } = useI18n();
const itemService = new ItemService();

const generateAutoPrice = useAutoSalePrice();
const pendingItem = ref<number|null>(null);
const tableParams = computed<TableComponentParams<ItemResource>>(() => {
  return {
    columns: [
      {
        name: 'name',
        data: (row) => row.short_name || row.name,
        title: t('fields.name'),
        sortable: true,
        responsiveOrder: 1,
      },
      {
        name: 'category.name',
        data: 'category.name',
        title: t('fields.category'),
        width: '18%',
        sortable: true,
      },
      {
        name: 'item_company.name',
        data: 'item_company.name',
        title: t('fields.item-company'),
        width: '18%',
        sortable: true,
      },
      {
        name: 'qty',
        data: (row: ItemResource) => {
          const unit = QtyUnitNameMap[row.qty_type];
          const unitName = t(`common.units.${unit}`);
          return `${row.qty} (${unitName})`;
        },
        title: t('fields.qty'),
        width: 120,
        cellClasses: 'text-gray-600 text-sm',
      },
      {
        name: 'price',
        data: (row) => coinsFormat(generateAutoPrice(row)),
        title: t('fields.sale_price_local'),
        width: 150,
        cellClasses: 'text-gray-600 text-sm',
      },
      {
        name: 'actions',
        title: '',
        width: 200,
        responsiveOrder: 2,
      },
    ],
  };
});
const tableManager = new TableServerDataManager({
  path: 'items/grid',
});

const selectedItemCompany = ref<ItemCompanyResource>();

const selectedCategory = ref<CategoryResource>();
const categoriesFilterManager = useSelectClientDataManager({
  data: computed(() => categoriesStore.data),
  hideSelected: true,
  selected: selectedCategory,
});

const filters = computed<Filters>(() => {
  const list: Filters = {};
  if (selectedCategory.value) {
    list['category.id'] = String(selectedCategory.value.id);
  }
  if (selectedItemCompany.value) {
    list['item_company.id'] = String(selectedItemCompany.value.id);
  }
  return list;
});
watch(filters, (value) => {
  tableManager.setFilters(value);
});

function checkMinStock(item: ItemResource) {
  const { qty, min_stock_qty } = item;
  return min_stock_qty && toNumberFormat(min_stock_qty) >= toNumberFormat(qty);
}
function removeItem(item: ItemResource) {
  modalsStore.addModal({
    component: ModalConfirmation,
    props: {
      title: t('pages.items.remove-label'),
      danger: true,
      confirmText: t('common.actions.delete'),
      confirm: () => {
        return itemService.remove(item.id).send().then(() => {
          tableManager.request();
        });
      },
    },
  });
}
function addItem() {
  modalsStore.addModal({
    component: ModalManageItem,
  });
}
async function editItem(id: number) {
  pendingItem.value = id;
  const { data } = await itemService.getById(String(id)).send().finally(() => {
    pendingItem.value = null;
  });
  modalsStore.addModal({
    component: ModalManageItem,
    props: {
      data,
    },
  });
}

useScanner(async (scanned) => {
  try {
    const { data } = await itemService.getByBarcode(scanned).send();
    modalsStore.addModal({
      component: ModalManageItem,
      props: {
        data,
      },
    });
  } catch (e: any) {
    if (e instanceof AxiosError && e.response) {
      const { response } = e;
      if (response.status === 404) {
        modalsStore.addModal({
          component: ModalManageItem,
          props: {
            barcode: scanned,
          },
        });
      }
    }
  }
});

function updateTable() {
  tableManager.request();
}
onMounted(() => {
  emitter.on('addItem', updateTable);
  emitter.on('updateItem', updateTable);
});
onBeforeUnmount(() => {
  emitter.off('addItem', updateTable);
  emitter.off('updateItem', updateTable);
});


return (_ctx: any,_cache: any) => {
  const _component_FontAwesomeIcon = _resolveComponent("FontAwesomeIcon")!

  return (_openBlock(), _createBlock(PageWrapper, {
    title: _ctx.$t('navigation.storage')
  }, {
    default: _withCtx(() => [
      _createVNode(Table, {
        "data-manager": _unref(tableManager),
        params: _unref(tableParams)
      }, {
        "header-left": _withCtx(() => [
          _createVNode(SingleSelect, {
            modelValue: selectedCategory.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedCategory).value = $event)),
            class: "w-64",
            "data-manager": _unref(categoriesFilterManager),
            "select-option-text": _ctx.$t('pages.items.select-category')
          }, null, 8, ["modelValue", "data-manager", "select-option-text"]),
          _createVNode(ItemCompanySelect, {
            modelValue: selectedItemCompany.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedItemCompany).value = $event)),
            class: "w-64"
          }, null, 8, ["modelValue"])
        ]),
        "header-right": _withCtx(() => [
          _createVNode(Button, {
            size: "xl",
            "button-type": "outline",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (addItem()))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_FontAwesomeIcon, {
                icon: "plus",
                class: "mr-2"
              }),
              _createTextVNode(_toDisplayString(_ctx.$t('common.actions.create_new')), 1)
            ]),
            _: 1
          })
        ]),
        "cell-qty": _withCtx(({ row, value }) => [
          _createElementVNode("span", {
            class: _normalizeClass({'text-danger': checkMinStock(row)})
          }, _toDisplayString(value), 3),
          (checkMinStock(row))
            ? (_openBlock(), _createBlock(Mark, {
                key: 0,
                class: "ml-1",
                icon: "question",
                color: "danger",
                "tooltip-message": _unref(t)(
            'pages.items.tooltips.min_stock_qty_reached',
            { qty: row.min_stock_qty },
          )
              }, null, 8, ["tooltip-message"]))
            : _createCommentVNode("", true)
        ]),
        "cell-actions": _withCtx(({ row }) => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(Button, {
              size: "sm",
              onClick: ($event: any) => (editItem(row.id)),
              loading: pendingItem.value === row.id
            }, {
              default: _withCtx(() => [
                _createVNode(_component_FontAwesomeIcon, {
                  icon: "pen",
                  class: "mr-2"
                }),
                _createTextVNode(_toDisplayString(_ctx.$t('common.actions.edit')), 1)
              ]),
              _: 2
            }, 1032, ["onClick", "loading"]),
            _createVNode(Button, {
              size: "sm",
              color: "danger",
              onClick: ($event: any) => (removeItem(row))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_FontAwesomeIcon, { icon: "trash" })
              ]),
              _: 2
            }, 1032, ["onClick"])
          ])
        ]),
        _: 1
      }, 8, ["data-manager", "params"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})